import { supabase } from './supabase'
import { resolveMedicalDocumentUrls } from './medical-document-storage'
import type {
  AbnormalFlag,
  LabResult,
  LabTestCategory,
  LabTrendData
} from '@/types/health'

type AnalysisStatus = 'normal' | 'anormal' | 'limite' | 'critique' | null

interface AnalysisResultRow {
  id: string
  document_id: string
  patient_id: string
  nom_analyse: string
  valeur: string | null
  unite: string | null
  valeur_reference: string | null
  statut: AnalysisStatus
  date_analyse: string
  laboratoire: string | null
  commentaire: string | null
  metadata: Record<string, unknown> | null
  created_at: string
  updated_at: string
}

interface MedicalDocumentLookup {
  id: string
  title: string
  file_name: string
  file_url: string | null
  practitioner_name: string | null
  medical_facility: string | null
  specialty: string | null
}

export interface LabMatrixCell {
  test_name: string
  result: LabResult
}

export interface LabMatrixRow {
  row_id: string
  test_date: string
  document_id: string | null
  document_title: string
  laboratory_name: string | null
  doctor_name: string | null
  specialty: string | null
  document_url: string | null
  cells: Record<string, LabMatrixCell>
}

export interface LabMatrixData {
  columns: string[]
  rows: LabMatrixRow[]
  filtered_results: LabResult[]
}

export interface LabDiseaseProfile {
  id: string
  label: string
  description: string
  categories: LabTestCategory[]
  tests: string[]
}

export interface ManualLabResultInput {
  patient_id: string
  test_date: string
  test_name: string
  result_value: string
  unit?: string | null
  reference_range?: string | null
  reference_min?: number | null
  reference_max?: number | null
  doctor_name?: string | null
  laboratory_name?: string | null
  specialty?: string | null
  notes?: string | null
}

function createLabDiseaseProfile(
  id: string,
  label: string,
  description: string,
  tests: string[],
  categories: LabTestCategory[] = []
): LabDiseaseProfile {
  return {
    id,
    label,
    description,
    categories,
    tests
  }
}

const LAB_NAME_ALIASES: Array<{
  canonical: string
  category: LabTestCategory
  patterns: RegExp[]
}> = [
  { canonical: 'CRP', category: 'Inflammation', patterns: [/^crp$/, /proteine c reactive/, /c reactive protein/] },
  { canonical: 'VS', category: 'Inflammation', patterns: [/^vs$/, /vitesse de sedimentation/] },
  { canonical: 'Ferritine', category: 'Fer', patterns: [/^ferritine$/, /^ferritin$/] },
  { canonical: 'Fer sérique', category: 'Fer', patterns: [/^fer$/, /fer serique/, /serum iron/] },
  { canonical: 'Transferrine', category: 'Fer', patterns: [/transferrine/] },
  { canonical: 'Hémoglobine', category: 'Hématologie', patterns: [/hemoglobine/, /^hb$/] },
  { canonical: 'Hématocrite', category: 'Hématologie', patterns: [/hematocrite/, /^htc$/] },
  { canonical: 'Leucocytes', category: 'Hématologie', patterns: [/leucocyte/, /globules blancs/, /^wbc$/] },
  { canonical: 'Neutrophiles', category: 'Hématologie', patterns: [/neutrophile/] },
  { canonical: 'Lymphocytes', category: 'Hématologie', patterns: [/lymphocyte/] },
  { canonical: 'Plaquettes', category: 'Hématologie', patterns: [/plaquette/, /platelet/] },
  { canonical: 'VGM', category: 'Hématologie', patterns: [/^vgm$/, /volume globulaire moyen/, /^mcv$/] },
  { canonical: 'TCMH', category: 'Hématologie', patterns: [/^tcmh$/, /teneur corpusculaire moyenne/, /^mch$/] },
  { canonical: 'CCMH', category: 'Hématologie', patterns: [/^ccmh$/, /concentration corpusculaire moyenne/, /^mchc$/] },
  { canonical: 'Glycémie', category: 'Métabolisme', patterns: [/glycemie/, /glucose/] },
  { canonical: 'HbA1c', category: 'Métabolisme', patterns: [/hba1c/, /hemoglobine glyquee/] },
  { canonical: 'Insuline', category: 'Métabolisme', patterns: [/insuline/] },
  { canonical: 'Cholestérol total', category: 'Lipides', patterns: [/cholesterol total/] },
  { canonical: 'HDL', category: 'Lipides', patterns: [/^hdl$/, /hdl cholesterol/] },
  { canonical: 'LDL', category: 'Lipides', patterns: [/^ldl$/, /ldl cholesterol/] },
  { canonical: 'Triglycérides', category: 'Lipides', patterns: [/triglyceride/] },
  { canonical: 'Créatinine', category: 'Rein', patterns: [/creatinine/] },
  { canonical: 'Urée', category: 'Rein', patterns: [/uree/] },
  { canonical: 'DFG', category: 'Rein', patterns: [/^dfg$/, /debit de filtration/, /egfr/] },
  { canonical: 'ALAT', category: 'Foie', patterns: [/^alat$/, /alanine aminotransferase/, /^alt$/] },
  { canonical: 'ASAT', category: 'Foie', patterns: [/^asat$/, /aspartate aminotransferase/, /^ast$/] },
  { canonical: 'Gamma GT', category: 'Foie', patterns: [/gamma gt/, /^ggt$/] },
  { canonical: 'Bilirubine', category: 'Foie', patterns: [/bilirubine/] },
  { canonical: 'TSH', category: 'Thyroïde', patterns: [/^tsh$/, /thyreostimuline/] },
  { canonical: 'T4 libre', category: 'Thyroïde', patterns: [/^t4 libre$/, /thyroxine libre/, /^ft4$/] },
  { canonical: 'T3 libre', category: 'Thyroïde', patterns: [/^t3 libre$/, /triiodothyronine libre/, /^ft3$/] },
  { canonical: 'Vitamine D', category: 'Vitamines', patterns: [/vitamine d/, /25 oh vitamine d/] },
  { canonical: 'Vitamine B12', category: 'Vitamines', patterns: [/vitamine b12/] },
  { canonical: 'Folates', category: 'Vitamines', patterns: [/folate/, /vitamine b9/] },
  { canonical: 'PSA', category: 'Hormones', patterns: [/^psa$/] },
  { canonical: 'FSH', category: 'Hormones', patterns: [/^fsh$/] },
  { canonical: 'LH', category: 'Hormones', patterns: [/^lh$/] },
  { canonical: 'Estradiol', category: 'Hormones', patterns: [/estradiol/] },
  { canonical: 'Progestérone', category: 'Hormones', patterns: [/progesterone/] }
]

const HEMATOLOGY_CATEGORY = LAB_NAME_ALIASES.find((entry) => entry.canonical === 'Leucocytes')?.category || 'Autre'
const METABOLISM_CATEGORY = LAB_NAME_ALIASES.find((entry) => entry.canonical === 'Insuline')?.category || 'Autre'
const THYROID_CATEGORY = LAB_NAME_ALIASES.find((entry) => entry.canonical === 'TSH')?.category || 'Autre'

const EXTRA_LAB_NAME_ALIASES: Array<{
  canonical: string
  category: LabTestCategory
  patterns: RegExp[]
}> = [
  { canonical: 'Hematies', category: HEMATOLOGY_CATEGORY, patterns: [/hematies/, /globules rouges/, /^rbc$/] },
  { canonical: 'Monocytes', category: HEMATOLOGY_CATEGORY, patterns: [/monocyte/] },
  { canonical: 'Polynucleaires neutrophiles', category: HEMATOLOGY_CATEGORY, patterns: [/polynucleaires? neutrophiles?/, /\bneutrophiles?\b/] },
  { canonical: 'Polynucleaires eosinophiles', category: HEMATOLOGY_CATEGORY, patterns: [/polynucleaires? eosinophiles?/, /\beosinophiles?\b/] },
  { canonical: 'Polynucleaires basophiles', category: HEMATOLOGY_CATEGORY, patterns: [/polynucleaires? basophiles?/, /\bbasophiles?\b/] },
  { canonical: 'IDR', category: HEMATOLOGY_CATEGORY, patterns: [/^idr$/, /\bi d r\b/, /distribution.*globules rouges/, /\brdw\b/] },
  { canonical: 'Albumine', category: METABOLISM_CATEGORY, patterns: [/^albumine$/, /albuminemie/] },
  { canonical: 'Proteines totales', category: METABOLISM_CATEGORY, patterns: [/proteines? totales?/] },
  { canonical: 'Calprotectine', category: 'Inflammation', patterns: [/calprotectine/] },
  { canonical: 'Phosphatases alcalines', category: 'Foie', patterns: [/phosphatases? alcalines?/] },
  { canonical: 'Calcium', category: METABOLISM_CATEGORY, patterns: [/^calcium$/] },
  { canonical: 'Magnesium', category: METABOLISM_CATEGORY, patterns: [/magnesium/] },
  { canonical: 'Sodium', category: 'Rein', patterns: [/sodium serique/, /^sodium$/] },
  { canonical: 'Potassium', category: 'Rein', patterns: [/potassium serique/, /^potassium$/] },
  { canonical: 'Chlore', category: 'Rein', patterns: [/chlore serique/, /^chlore$/] },
  { canonical: 'Cortisol', category: 'Hormones', patterns: [/cortisol/] },
  { canonical: 'Immunoglobulines A', category: 'Immunologie', patterns: [/immunoglobulines? a/, /^iga$/] },
  { canonical: 'Immunoglobulines G', category: 'Immunologie', patterns: [/immunoglobulines? g/, /^igg$/] },
  { canonical: 'Immunoglobulines M', category: 'Immunologie', patterns: [/immunoglobulines? m/, /^igm$/] }
]

const ALL_LAB_NAME_ALIASES = [...LAB_NAME_ALIASES, ...EXTRA_LAB_NAME_ALIASES]
const AVAILABLE_LAB_CATEGORIES = [
  ...new Set<LabTestCategory>([...ALL_LAB_NAME_ALIASES.map((entry) => entry.category), 'Autre'])
]

const LEGACY_LAB_DISEASE_PROFILES: LabDiseaseProfile[] = [
  {
    id: 'rch',
    label: 'RCH / inflammation digestive',
    description: 'Inflammation, anemie, carences et retentissement digestif.',
    categories: ['Inflammation', 'Fer', 'Hématologie', 'Vitamines', 'Métabolisme'],
    tests: ['CRP', 'VS', 'Ferritine', 'Fer sÃ©rique', 'Transferrine', 'HÃ©moglobine', 'Leucocytes', 'Plaquettes', 'Albumine', 'Vitamine D', 'Vitamine B12', 'Folates']
  },
  {
    id: 'endometriose',
    label: 'Endometriose / bilan gyneco',
    description: 'Inflammation, pertes sanguines, reserve martiale et bilan hormonal.',
    categories: ['Inflammation', 'Fer', 'Hématologie', 'Hormones', 'Vitamines'],
    tests: ['CRP', 'VS', 'Ferritine', 'Fer sÃ©rique', 'Transferrine', 'HÃ©moglobine', 'FSH', 'LH', 'Estradiol', 'ProgestÃ©rone', 'Vitamine D']
  },
  {
    id: 'fatigue-carences',
    label: 'Fatigue / carences',
    description: 'Bilan simple des carences et causes biologiques frequentes.',
    categories: ['Fer', 'Hématologie', 'Vitamines', 'Thyroïde', 'Métabolisme'],
    tests: ['Ferritine', 'Fer sÃ©rique', 'Transferrine', 'HÃ©moglobine', 'Vitamine D', 'Vitamine B12', 'Folates', 'TSH', 'GlycÃ©mie']
  }
]

export const LAB_DISEASE_PROFILES: LabDiseaseProfile[] = [
  createLabDiseaseProfile(
    'rch',
    'RCH',
    'Inflammation digestive, anemie, carences et retentissement nutritionnel.',
    ['CRP', 'VS', 'Ferritine', 'Fer sérique', 'Transferrine', 'Hémoglobine', 'Leucocytes', 'Plaquettes', 'Albumine', 'Vitamine D', 'Vitamine B12', 'Folates'],
    ['Inflammation', 'Fer', 'Hématologie', 'Vitamines', 'Métabolisme']
  ),
  createLabDiseaseProfile(
    'endometriose',
    'Endometriose',
    'Inflammation, reserve martiale, pertes sanguines et bilan hormonal.',
    ['CRP', 'VS', 'Ferritine', 'Fer sérique', 'Transferrine', 'Hémoglobine', 'FSH', 'LH', 'Estradiol', 'Progestérone', 'Vitamine D'],
    ['Inflammation', 'Fer', 'Hématologie', 'Hormones', 'Vitamines']
  ),
  createLabDiseaseProfile(
    'fatigue-carences',
    'Fatigue / carences',
    'Carences, thyroide et causes biologiques frequentes de fatigue.',
    ['Ferritine', 'Fer sérique', 'Transferrine', 'Hémoglobine', 'Vitamine D', 'Vitamine B12', 'Folates', 'TSH', 'Glycémie'],
    ['Fer', 'Hématologie', 'Vitamines', 'Thyroïde', 'Métabolisme']
  ),
  createLabDiseaseProfile(
    'anemie-fer',
    'Anemie / fer',
    'Surveillance de l anemie, du volume globulaire et des reserves martiales.',
    ['Hémoglobine', 'Hématocrite', 'VGM', 'TCMH', 'CCMH', 'Ferritine', 'Fer sérique', 'Transferrine']
  ),
  createLabDiseaseProfile(
    'thyroide',
    'Thyroide',
    'Bilan thyroidien simple.',
    ['TSH', 'T4 libre', 'T3 libre']
  ),
  createLabDiseaseProfile(
    'rein',
    'Rein',
    'Fonction renale et filtration.',
    ['Créatinine', 'Urée', 'DFG']
  ),
  createLabDiseaseProfile(
    'foie',
    'Foie',
    'Cytolyse, cholestase et bilirubine.',
    ['ALAT', 'ASAT', 'Gamma GT', 'Bilirubine']
  ),
  createLabDiseaseProfile(
    'metabolisme',
    'Metabolisme / glycemie',
    'Glycemie, insuline et equilibre metabolique.',
    ['Glycémie', 'HbA1c', 'Insuline']
  ),
  createLabDiseaseProfile(
    'nutrition',
    'Nutrition / vitamines',
    'Albumine, vitamines et retentissement nutritionnel.',
    ['Albumine', 'Vitamine D', 'Vitamine B12', 'Folates']
  ),
  createLabDiseaseProfile(
    'immunologie',
    'Immunologie / serologies',
    'Auto-immunite, infections et bilan immunologique.',
    ['Immunoglobulines A', 'Immunoglobulines G', 'Immunoglobulines M', 'Calprotectine', 'Cortisol'],
    ['Immunologie', 'Inflammation', 'Hormones']
  ),
  createLabDiseaseProfile(
    'ionogramme',
    'Ionogramme',
    'Equilibre hydro-electrolytique et bilan mineral.',
    ['Sodium', 'Potassium', 'Chlore', 'Calcium', 'Magnesium'],
    ['Rein', METABOLISM_CATEGORY]
  ),
  createLabDiseaseProfile(
    'nfs',
    'NFS / hematologie',
    'Globules rouges, blancs, plaquettes et formule leucocytaire.',
    ['Hematies', 'Hemoglobine', 'Hematocrite', 'Leucocytes', 'Plaquettes', 'Polynucleaires neutrophiles', 'Polynucleaires eosinophiles', 'Polynucleaires basophiles', 'Lymphocytes', 'Monocytes', 'VGM', 'TCMH', 'CCMH', 'IDR'],
    [HEMATOLOGY_CATEGORY]
  )
]

void LEGACY_LAB_DISEASE_PROFILES

function normalizeSearchText(value: string | null | undefined): string {
  return (value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
}

function titleCaseLabel(value: string): string {
  return value
    .split(' ')
    .filter(Boolean)
    .map((segment) => {
      if (/^[A-Z0-9-]{2,6}$/.test(segment)) {
        return segment
      }

      return segment.charAt(0).toUpperCase() + segment.slice(1).toLowerCase()
    })
    .join(' ')
}

function applyLabNameQualifier(canonicalName: string, sourceName: string): string {
  const normalizedCanonicalName = normalizeSearchText(canonicalName)
  const normalizedSourceName = normalizeSearchText(sourceName)

  if ((sourceName.includes('%') || /\bpourcent(?:age)?\b/.test(normalizedSourceName)) && !canonicalName.includes('%')) {
    return `${canonicalName} %`
  }

  if (/\blibre\b/.test(normalizedSourceName) && !/\blibre\b/.test(normalizedCanonicalName)) {
    return `${canonicalName} libre`
  }

  if (/\btotal(?:e|es)?\b/.test(normalizedSourceName) && !/\btotal(?:e|es)?\b/.test(normalizedCanonicalName)) {
    return `${canonicalName} total`
  }

  return canonicalName
}

export function normalizeLabTestName(name: string | null | undefined): string {
  const source = (name || '').trim()

  if (!source) {
    return 'Analyse non nommée'
  }

  const normalized = normalizeSearchText(source)

  for (const alias of ALL_LAB_NAME_ALIASES) {
    if (alias.patterns.some((pattern) => pattern.test(normalized))) {
      return applyLabNameQualifier(alias.canonical, source)
    }
  }

  return titleCaseLabel(source.replace(/\s+/g, ' '))
}

export function inferLabTestCategory(testName: string): LabTestCategory {
  const normalized = normalizeSearchText(testName)
  const alias = ALL_LAB_NAME_ALIASES.find((entry) => {
    if (entry.canonical === testName) {
      return true
    }

    return applyLabNameQualifier(entry.canonical, testName) === testName
  })

  if (alias) {
    return alias.category
  }

  if (/crp|sedimentation|fibrinogene/.test(normalized)) return 'Inflammation'
  if (/ferrit|transferr|fer/.test(normalized)) return 'Fer'
  if (/hemoglob|hemat|leuc|lymph|plaquette|neutro|monocyte|eosinophile|basophile|vgm|tcmh|ccmh/.test(normalized)) return 'Hématologie'
  if (/glycem|glucose|insuline|hba1c/.test(normalized)) return 'Métabolisme'
  if (/cholesterol|hdl|ldl|triglycer/.test(normalized)) return 'Lipides'
  if (/creatin|uree|dfg|proteinurie/.test(normalized)) return 'Rein'
  if (/alat|asat|gamma|ggt|bilirub|phosphatase/.test(normalized)) return 'Foie'
  if (/tsh|t3|t4|thyro/.test(normalized)) return 'Thyroïde'
  if (/vitamine|folate|b12/.test(normalized)) return 'Vitamines'
  if (/hormone|fsh|lh|estradiol|progesterone|testosterone|prolactine|psa/.test(normalized)) return 'Hormones'
  if (/anticorps|igg|iga|igm|serologie/.test(normalized)) return 'Immunologie'

  return 'Autre'
}

function coerceLabTestCategory(value: unknown): LabTestCategory | null {
  if (typeof value !== 'string') {
    return null
  }

  const trimmedValue = value.trim()
  return AVAILABLE_LAB_CATEGORIES.find((category) => category === trimmedValue) || null
}

interface AnalysisRowMetadata {
  canonical_name?: unknown
  raw_name?: unknown
  test_category?: unknown
  reference_range?: unknown
  laboratory_name?: unknown
  doctor_name?: unknown
  specialty?: unknown
}

function getAnalysisRowMetadata(row: AnalysisResultRow): AnalysisRowMetadata {
  if (!row.metadata || Array.isArray(row.metadata)) {
    return {}
  }

  return row.metadata
}

function pickBestString(...values: unknown[]): string | null {
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) {
      return value.trim()
    }
  }

  return null
}

type LabUnitStatus = 'normalized' | 'converted' | 'unsupported' | 'missing' | 'raw'

interface LabUnitRule {
  canonicalUnit: string
  matches: RegExp[]
  toCanonical?: (value: number) => number
}

interface LabUnitProfile {
  id: string
  testPatterns: RegExp[]
  rules: LabUnitRule[]
}

interface NormalizedLabMeasurement {
  rawUnit: string | null
  normalizedUnit: string | null
  normalizedValue: number | null
  normalizedReferenceMin: number | null
  normalizedReferenceMax: number | null
  unitGroupKey: string
  unitGroupLabel: string
  unitStatus: LabUnitStatus
  isUnitConverted: boolean
  unitWarning: string | null
}

export interface LabUnitMixWarning {
  test_name: string
  unit_labels: string[]
  converted_results: number
  missing_unit_results: number
  unsupported_unit_results: number
}

export interface LabUnitInsightSummary {
  converted_results_count: number
  missing_unit_results_count: number
  unsupported_unit_results_count: number
  mixed_unit_tests: LabUnitMixWarning[]
}

const identityUnitValue = (value: number) => value
const scaleUnitValue = (factor: number) => (value: number) => value * factor
const affineUnitValue = (factor: number, offset: number) => (value: number) => value * factor + offset

const DIFFERENTIAL_PERCENT_PATTERNS = [
  /neutrophiles$/,
  /lymphocytes$/,
  /monocytes$/,
  /eosinophiles$/,
  /basophiles$/,
  /polynucleaires neutrophiles$/,
  /polynucleaires eosinophiles$/,
  /polynucleaires basophiles$/
]

const LAB_UNIT_PROFILES: LabUnitProfile[] = [
  {
    id: 'crp',
    testPatterns: [/^crp$/],
    rules: [
      { canonicalUnit: 'mg/L', matches: [/^mg\/l$/, /^mgl$/], toCanonical: identityUnitValue },
      { canonicalUnit: 'mg/L', matches: [/^mg\/dl$/, /^mgdl$/], toCanonical: scaleUnitValue(10) },
      { canonicalUnit: 'mg/L', matches: [/^g\/l$/, /^gl$/], toCanonical: scaleUnitValue(1000) }
    ]
  },
  {
    id: 'ferritine',
    testPatterns: [/^ferritine$/],
    rules: [
      { canonicalUnit: 'ug/L', matches: [/^ug\/l$/, /^mcg\/l$/, /^microg\/l$/], toCanonical: identityUnitValue },
      { canonicalUnit: 'ug/L', matches: [/^ng\/ml$/], toCanonical: identityUnitValue },
      { canonicalUnit: 'ug/L', matches: [/^ng\/l$/], toCanonical: scaleUnitValue(0.001) }
    ]
  },
  {
    id: 'fer-serique',
    testPatterns: [/^fer serique$/],
    rules: [
      { canonicalUnit: 'umol/L', matches: [/^umol\/l$/, /^micromol\/l$/], toCanonical: identityUnitValue },
      { canonicalUnit: 'umol/L', matches: [/^ug\/dl$/, /^mcg\/dl$/], toCanonical: scaleUnitValue(0.179) },
      { canonicalUnit: 'umol/L', matches: [/^mg\/l$/, /^mgl$/], toCanonical: scaleUnitValue(17.9) }
    ]
  },
  {
    id: 'transferrine',
    testPatterns: [/^transferrine$/],
    rules: [
      { canonicalUnit: 'g/L', matches: [/^g\/l$/, /^gl$/], toCanonical: identityUnitValue },
      { canonicalUnit: 'g/L', matches: [/^g\/dl$/, /^gdl$/], toCanonical: scaleUnitValue(10) },
      { canonicalUnit: 'g/L', matches: [/^mg\/dl$/, /^mgdl$/], toCanonical: scaleUnitValue(0.01) }
    ]
  },
  {
    id: 'hemoglobine',
    testPatterns: [/^hemoglobine$/],
    rules: [
      { canonicalUnit: 'g/L', matches: [/^g\/l$/, /^gl$/], toCanonical: identityUnitValue },
      { canonicalUnit: 'g/L', matches: [/^g\/dl$/, /^gdl$/], toCanonical: scaleUnitValue(10) }
    ]
  },
  {
    id: 'hematocrite',
    testPatterns: [/^hematocrite$/],
    rules: [
      { canonicalUnit: '%', matches: [/^%$/, /^pct$/], toCanonical: identityUnitValue },
      { canonicalUnit: '%', matches: [/^l\/l$/], toCanonical: scaleUnitValue(100) }
    ]
  },
  {
    id: 'hematies',
    testPatterns: [/^hematies$/],
    rules: [
      { canonicalUnit: 'T/L', matches: [/^t\/l$/, /^10\^12\/l$/, /^10x12\/l$/, /^10e12\/l$/, /^m\/mm3$/, /^10\^6\/mm3$/], toCanonical: identityUnitValue },
      { canonicalUnit: 'T/L', matches: [/^\/mm3$/, /^\/ul$/], toCanonical: scaleUnitValue(0.000001) }
    ]
  },
  {
    id: 'cell-count-absolute',
    testPatterns: [
      /^leucocytes$/,
      /^plaquettes$/,
      /^neutrophiles$/,
      /^lymphocytes$/,
      /^monocytes$/,
      /^eosinophiles$/,
      /^basophiles$/,
      /^polynucleaires neutrophiles$/,
      /^polynucleaires eosinophiles$/,
      /^polynucleaires basophiles$/
    ],
    rules: [
      { canonicalUnit: 'G/L', matches: [/^g\/l$/, /^10\^9\/l$/, /^10x9\/l$/, /^10e9\/l$/, /^10\^3\/mm3$/, /^k\/ul$/], toCanonical: identityUnitValue },
      { canonicalUnit: 'G/L', matches: [/^\/mm3$/, /^\/ul$/], toCanonical: scaleUnitValue(0.001) }
    ]
  },
  {
    id: 'cell-count-percent',
    testPatterns: [
      /^neutrophiles %$/,
      /^lymphocytes %$/,
      /^monocytes %$/,
      /^eosinophiles %$/,
      /^basophiles %$/,
      /^polynucleaires neutrophiles %$/,
      /^polynucleaires eosinophiles %$/,
      /^polynucleaires basophiles %$/
    ],
    rules: [{ canonicalUnit: '%', matches: [/^%$/, /^pct$/], toCanonical: identityUnitValue }]
  },
  {
    id: 'vgm',
    testPatterns: [/^vgm$/],
    rules: [{ canonicalUnit: 'fL', matches: [/^fl$/], toCanonical: identityUnitValue }]
  },
  {
    id: 'tcmh',
    testPatterns: [/^tcmh$/],
    rules: [{ canonicalUnit: 'pg', matches: [/^pg$/], toCanonical: identityUnitValue }]
  },
  {
    id: 'ccmh',
    testPatterns: [/^ccmh$/],
    rules: [
      { canonicalUnit: 'g/dL', matches: [/^g\/dl$/, /^gdl$/], toCanonical: identityUnitValue },
      { canonicalUnit: 'g/dL', matches: [/^g\/l$/, /^gl$/], toCanonical: scaleUnitValue(0.1) }
    ]
  },
  {
    id: 'albumine',
    testPatterns: [/^albumine$/],
    rules: [
      { canonicalUnit: 'g/L', matches: [/^g\/l$/, /^gl$/], toCanonical: identityUnitValue },
      { canonicalUnit: 'g/L', matches: [/^g\/dl$/, /^gdl$/], toCanonical: scaleUnitValue(10) }
    ]
  },
  {
    id: 'proteines-totales',
    testPatterns: [/^proteines totales$/],
    rules: [
      { canonicalUnit: 'g/L', matches: [/^g\/l$/, /^gl$/], toCanonical: identityUnitValue },
      { canonicalUnit: 'g/L', matches: [/^g\/dl$/, /^gdl$/], toCanonical: scaleUnitValue(10) }
    ]
  },
  {
    id: 'glycemie',
    testPatterns: [/^glycemie$/],
    rules: [
      { canonicalUnit: 'g/L', matches: [/^g\/l$/, /^gl$/], toCanonical: identityUnitValue },
      { canonicalUnit: 'g/L', matches: [/^mg\/dl$/, /^mgdl$/], toCanonical: scaleUnitValue(0.01) },
      { canonicalUnit: 'g/L', matches: [/^mmol\/l$/], toCanonical: scaleUnitValue(0.18016) }
    ]
  },
  {
    id: 'hba1c',
    testPatterns: [/^hba1c$/],
    rules: [
      { canonicalUnit: '%', matches: [/^%$/, /^pct$/], toCanonical: identityUnitValue },
      { canonicalUnit: '%', matches: [/^mmol\/mol$/], toCanonical: affineUnitValue(0.09148, 2.152) }
    ]
  },
  {
    id: 'creatinine',
    testPatterns: [/^creatinine$/],
    rules: [
      { canonicalUnit: 'umol/L', matches: [/^umol\/l$/, /^micromol\/l$/], toCanonical: identityUnitValue },
      { canonicalUnit: 'umol/L', matches: [/^mg\/l$/, /^mgl$/], toCanonical: scaleUnitValue(8.84) },
      { canonicalUnit: 'umol/L', matches: [/^mg\/dl$/, /^mgdl$/], toCanonical: scaleUnitValue(88.4) }
    ]
  },
  {
    id: 'uree',
    testPatterns: [/^uree$/],
    rules: [
      { canonicalUnit: 'mmol/L', matches: [/^mmol\/l$/], toCanonical: identityUnitValue },
      { canonicalUnit: 'mmol/L', matches: [/^g\/l$/, /^gl$/], toCanonical: scaleUnitValue(16.65) },
      { canonicalUnit: 'mmol/L', matches: [/^mg\/dl$/, /^mgdl$/], toCanonical: scaleUnitValue(0.1665) }
    ]
  },
  {
    id: 'dfg',
    testPatterns: [/^dfg$/],
    rules: [
      {
        canonicalUnit: 'mL/min/1.73m2',
        matches: [/^ml\/min\/1\.73m2$/, /^ml\/min\/1,73m2$/, /^ml\/min$/, /^ml\/mn$/],
        toCanonical: identityUnitValue
      }
    ]
  },
  {
    id: 'liver-enzymes',
    testPatterns: [/^alat$/, /^asat$/, /^gamma gt$/, /^phosphatases alcalines$/],
    rules: [
      { canonicalUnit: 'UI/L', matches: [/^ui\/l$/, /^iu\/l$/, /^u\/l$/], toCanonical: identityUnitValue }
    ]
  },
  {
    id: 'bilirubine',
    testPatterns: [/^bilirubine$/],
    rules: [
      { canonicalUnit: 'umol/L', matches: [/^umol\/l$/, /^micromol\/l$/], toCanonical: identityUnitValue },
      { canonicalUnit: 'umol/L', matches: [/^mg\/l$/, /^mgl$/], toCanonical: scaleUnitValue(1.71) },
      { canonicalUnit: 'umol/L', matches: [/^mg\/dl$/, /^mgdl$/], toCanonical: scaleUnitValue(17.1) }
    ]
  },
  {
    id: 'tsh',
    testPatterns: [/^tsh$/],
    rules: [
      { canonicalUnit: 'mUI/L', matches: [/^mui\/l$/, /^miu\/l$/, /^uiu\/ml$/, /^uui\/ml$/], toCanonical: identityUnitValue }
    ]
  },
  {
    id: 't4-libre',
    testPatterns: [/^t4 libre$/],
    rules: [
      { canonicalUnit: 'pmol/L', matches: [/^pmol\/l$/], toCanonical: identityUnitValue },
      { canonicalUnit: 'pmol/L', matches: [/^ng\/dl$/, /^ngdl$/], toCanonical: scaleUnitValue(12.87) }
    ]
  },
  {
    id: 't3-libre',
    testPatterns: [/^t3 libre$/],
    rules: [
      { canonicalUnit: 'pmol/L', matches: [/^pmol\/l$/], toCanonical: identityUnitValue },
      { canonicalUnit: 'pmol/L', matches: [/^pg\/ml$/, /^ng\/l$/], toCanonical: scaleUnitValue(1.536) }
    ]
  },
  {
    id: 'vitamine-d',
    testPatterns: [/^vitamine d$/],
    rules: [
      { canonicalUnit: 'ng/mL', matches: [/^ng\/ml$/], toCanonical: identityUnitValue },
      { canonicalUnit: 'ng/mL', matches: [/^nmol\/l$/], toCanonical: scaleUnitValue(0.40064) }
    ]
  },
  {
    id: 'vitamine-b12',
    testPatterns: [/^vitamine b12$/],
    rules: [
      { canonicalUnit: 'pg/mL', matches: [/^pg\/ml$/], toCanonical: identityUnitValue },
      { canonicalUnit: 'pg/mL', matches: [/^pmol\/l$/], toCanonical: scaleUnitValue(1 / 0.738) }
    ]
  },
  {
    id: 'folates',
    testPatterns: [/^folates?$/],
    rules: [
      { canonicalUnit: 'ng/mL', matches: [/^ng\/ml$/], toCanonical: identityUnitValue },
      { canonicalUnit: 'ng/mL', matches: [/^nmol\/l$/], toCanonical: scaleUnitValue(1 / 2.266) }
    ]
  },
  {
    id: 'electrolytes',
    testPatterns: [/^sodium$/, /^potassium$/, /^chlore$/],
    rules: [
      { canonicalUnit: 'mmol/L', matches: [/^mmol\/l$/, /^meq\/l$/], toCanonical: identityUnitValue }
    ]
  },
  {
    id: 'calcium',
    testPatterns: [/^calcium$/],
    rules: [
      { canonicalUnit: 'mmol/L', matches: [/^mmol\/l$/], toCanonical: identityUnitValue },
      { canonicalUnit: 'mmol/L', matches: [/^mg\/l$/, /^mgl$/], toCanonical: scaleUnitValue(1 / 40.078) },
      { canonicalUnit: 'mmol/L', matches: [/^mg\/dl$/, /^mgdl$/], toCanonical: scaleUnitValue(10 / 40.078) }
    ]
  },
  {
    id: 'magnesium',
    testPatterns: [/^magnesium$/],
    rules: [
      { canonicalUnit: 'mmol/L', matches: [/^mmol\/l$/], toCanonical: identityUnitValue },
      { canonicalUnit: 'mmol/L', matches: [/^mg\/l$/, /^mgl$/], toCanonical: scaleUnitValue(1 / 24.305) },
      { canonicalUnit: 'mmol/L', matches: [/^mg\/dl$/, /^mgdl$/], toCanonical: scaleUnitValue(10 / 24.305) }
    ]
  },
  {
    id: 'immunoglobulines',
    testPatterns: [/^immunoglobulines a$/, /^immunoglobulines g$/, /^immunoglobulines m$/],
    rules: [
      { canonicalUnit: 'g/L', matches: [/^g\/l$/, /^gl$/], toCanonical: identityUnitValue },
      { canonicalUnit: 'g/L', matches: [/^mg\/dl$/, /^mgdl$/], toCanonical: scaleUnitValue(0.01) }
    ]
  },
  {
    id: 'cortisol',
    testPatterns: [/^cortisol$/],
    rules: [
      { canonicalUnit: 'nmol/L', matches: [/^nmol\/l$/], toCanonical: identityUnitValue },
      { canonicalUnit: 'nmol/L', matches: [/^ug\/dl$/, /^mcg\/dl$/], toCanonical: scaleUnitValue(27.59) }
    ]
  },
  {
    id: 'calprotectine',
    testPatterns: [/^calprotectine$/],
    rules: [
      { canonicalUnit: 'ug/g', matches: [/^ug\/g$/, /^mcg\/g$/], toCanonical: identityUnitValue },
      { canonicalUnit: 'ug/g', matches: [/^mg\/kg$/], toCanonical: identityUnitValue }
    ]
  }
]

function normalizeUnitToken(value: string | null | undefined): string {
  return (value || '')
    .normalize('NFKD')
    .replace(/[µμ]/g, 'u')
    .replace(/[²]/g, '2')
    .replace(/[³]/g, '3')
    .replace(/[–—−]/g, '-')
    .replace(/litres?|liters?/g, 'l')
    .replace(/par/g, '/')
    .replace(/[·]/g, '/')
    .replace(/\s+/g, '')
    .toLowerCase()
}

function roundMedicalValue(value: number | null): number | null {
  if (value === null || Number.isNaN(value)) {
    return null
  }

  return Math.round(value * 1000000) / 1000000
}

function convertNullableNumber(
  value: number | null,
  converter: (value: number) => number
): number | null {
  if (value === null || Number.isNaN(value)) {
    return null
  }

  return roundMedicalValue(converter(value))
}

function applyLabUnitQualifier(testName: string, unit: string | null | undefined): string {
  const normalizedTestName = normalizeSearchText(testName)
  const normalizedUnit = normalizeUnitToken(unit)

  if (
    normalizedUnit === '%' &&
    !testName.includes('%') &&
    DIFFERENTIAL_PERCENT_PATTERNS.some((pattern) => pattern.test(normalizedTestName))
  ) {
    return `${testName} %`
  }

  return testName
}

function getLabUnitProfile(testName: string): LabUnitProfile | undefined {
  const normalizedTestName = normalizeSearchText(testName)

  return LAB_UNIT_PROFILES.find((profile) =>
    profile.testPatterns.some((pattern) => pattern.test(normalizedTestName))
  )
}

function normalizeLabMeasurement(
  testName: string,
  value: number | null,
  rawUnit: string | null,
  referenceMin: number | null,
  referenceMax: number | null
): NormalizedLabMeasurement {
  const rawUnitLabel = rawUnit?.trim() || null
  const normalizedUnitToken = normalizeUnitToken(rawUnitLabel)
  const profile = getLabUnitProfile(testName)

  if (!rawUnitLabel) {
    return {
      rawUnit: null,
      normalizedUnit: null,
      normalizedValue: value,
      normalizedReferenceMin: referenceMin,
      normalizedReferenceMax: referenceMax,
      unitGroupKey: `${testName}::missing-unit`,
      unitGroupLabel: 'Sans unite',
      unitStatus: 'missing',
      isUnitConverted: false,
      unitWarning: 'Unite absente: comparaison prudente.'
    }
  }

  if (!profile) {
    return {
      rawUnit: rawUnitLabel,
      normalizedUnit: rawUnitLabel,
      normalizedValue: value,
      normalizedReferenceMin: referenceMin,
      normalizedReferenceMax: referenceMax,
      unitGroupKey: `${testName}::${normalizedUnitToken || rawUnitLabel}`,
      unitGroupLabel: rawUnitLabel,
      unitStatus: 'raw',
      isUnitConverted: false,
      unitWarning: null
    }
  }

  const matchingRule = profile.rules.find((rule) =>
    rule.matches.some((pattern) => pattern.test(normalizedUnitToken))
  )

  if (!matchingRule) {
    return {
      rawUnit: rawUnitLabel,
      normalizedUnit: rawUnitLabel,
      normalizedValue: value,
      normalizedReferenceMin: referenceMin,
      normalizedReferenceMax: referenceMax,
      unitGroupKey: `${testName}::unsupported::${normalizedUnitToken || rawUnitLabel}`,
      unitGroupLabel: rawUnitLabel,
      unitStatus: 'unsupported',
      isUnitConverted: false,
      unitWarning: `Unite ${rawUnitLabel} non harmonisee pour cette analyse.`
    }
  }

  const converter = matchingRule.toCanonical || identityUnitValue
  const normalizedValue = convertNullableNumber(value, converter)
  const normalizedReferenceMin = convertNullableNumber(referenceMin, converter)
  const normalizedReferenceMax = convertNullableNumber(referenceMax, converter)
  const isUnitConverted =
    normalizedUnitToken !== normalizeUnitToken(matchingRule.canonicalUnit) ||
    (value !== null && normalizedValue !== null && normalizedValue !== value)

  return {
    rawUnit: rawUnitLabel,
    normalizedUnit: matchingRule.canonicalUnit,
    normalizedValue,
    normalizedReferenceMin,
    normalizedReferenceMax,
    unitGroupKey: `${testName}::${profile.id}::${matchingRule.canonicalUnit}`,
    unitGroupLabel: matchingRule.canonicalUnit,
    unitStatus: isUnitConverted ? 'converted' : 'normalized',
    isUnitConverted,
    unitWarning: isUnitConverted
      ? `Conversion automatique ${rawUnitLabel} -> ${matchingRule.canonicalUnit} pour comparer les tendances.`
      : null
  }
}

function parseMedicalNumber(value: string | number | null | undefined): number | null {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null
  }

  if (!value) {
    return null
  }

  const normalized = value
    .replace(/\s+/g, '')
    .replace(',', '.')
    .replace(/(?<=\d)\.(?=\d{3}(?:\D|$))/g, '')

  const match = normalized.match(/-?\d+(?:\.\d+)?/)
  if (!match) {
    return null
  }

  const parsed = Number.parseFloat(match[0])
  return Number.isFinite(parsed) ? parsed : null
}

function parseReferenceRange(reference: string | null | undefined): {
  reference_min: number | null
  reference_max: number | null
} {
  if (!reference) {
    return { reference_min: null, reference_max: null }
  }

  const normalized = reference.replace(',', '.').replace(/\s+/g, ' ').trim()
  const rangeMatch = normalized.match(/(-?\d+(?:\.\d+)?)\s*(?:-|–|—|a|à)\s*(-?\d+(?:\.\d+)?)/i)

  if (rangeMatch) {
    return {
      reference_min: Number.parseFloat(rangeMatch[1]),
      reference_max: Number.parseFloat(rangeMatch[2])
    }
  }

  const maxMatch = normalized.match(/(?:<|<=|inf(?:erieur)?(?: a)?|jusqu'?a)\s*(-?\d+(?:\.\d+)?)/i)
  if (maxMatch) {
    return {
      reference_min: null,
      reference_max: Number.parseFloat(maxMatch[1])
    }
  }

  const minMatch = normalized.match(/(?:>|>=|sup(?:erieur)?(?: a)?|a partir de)\s*(-?\d+(?:\.\d+)?)/i)
  if (minMatch) {
    return {
      reference_min: Number.parseFloat(minMatch[1]),
      reference_max: null
    }
  }

  return { reference_min: null, reference_max: null }
}

function calculateAbnormalFlagFromRange(
  value: number | null,
  referenceMin: number | null,
  referenceMax: number | null
): AbnormalFlag | null {
  if (value === null) {
    return null
  }

  if (referenceMin !== null && value < referenceMin) {
    return value < referenceMin * 0.5 ? 'LL' : 'L'
  }

  if (referenceMax !== null && value > referenceMax) {
    return value > referenceMax * 2 ? 'HH' : 'H'
  }

  return null
}

function buildReferenceRangeLabel(
  referenceMin: number | null,
  referenceMax: number | null,
  fallbackValue?: string | null
): string | null {
  const normalizedFallback = typeof fallbackValue === 'string' ? fallbackValue.trim() : ''

  if (normalizedFallback) {
    return normalizedFallback
  }

  if (referenceMin !== null && referenceMax !== null) {
    return `${referenceMin} - ${referenceMax}`
  }

  if (referenceMax !== null) {
    return `< ${referenceMax}`
  }

  if (referenceMin !== null) {
    return `> ${referenceMin}`
  }

  return null
}

function mapAbnormalFlagToStatus(flag: AbnormalFlag | null): AnalysisStatus {
  if (flag === 'HH' || flag === 'LL') {
    return 'critique'
  }

  if (flag === 'H' || flag === 'L') {
    return 'anormal'
  }

  return 'normal'
}

function slugifyManualLabTestName(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .toLowerCase()
    .slice(0, 40) || 'analyse'
}

async function syncManualLabCatalogRow(params: {
  documentId: string
  patientId: string
  testDate: string
  rawName: string
  canonicalName: string
  testCategory: LabTestCategory
  resultValue: string
  unit: string | null
  referenceRange: string | null
  status: AnalysisStatus
  laboratoryName: string | null
  doctorName: string | null
  specialty: string | null
}) {
  const metadata = {
    raw_name: params.rawName,
    canonical_name: params.canonicalName,
    normalized_search: normalizeSearchText(params.rawName),
    raw_value: params.resultValue,
    unit: params.unit,
    reference_range: params.referenceRange,
    status: params.status,
    test_category: params.testCategory,
    source_document_type: 'blood_test',
    laboratory_name: params.laboratoryName,
    doctor_name: params.doctorName,
    specialty: params.specialty,
    entry_mode: 'manual'
  }

  const { error } = await supabase
    .from('patient_lab_test_catalog')
    .upsert(
      [
        {
          patient_id: params.patientId,
          raw_name: params.rawName,
          canonical_name: params.canonicalName,
          normalized_search: metadata.normalized_search,
          test_category: params.testCategory,
          last_unit: params.unit,
          last_reference_range: params.referenceRange,
          last_value: params.resultValue,
          last_status: params.status,
          last_analysis_date: params.testDate,
          source_document_id: params.documentId,
          laboratory_name: params.laboratoryName,
          doctor_name: params.doctorName,
          metadata
        }
      ],
      { onConflict: 'patient_id,canonical_name,raw_name' }
    )

  if (!error) {
    return
  }

  const message = error.message || ''
  const isMissingTable =
    /relation .*patient_lab_test_catalog.* does not exist/i.test(message) ||
    /Could not find the table/i.test(message)

  if (!isMissingTable) {
    console.warn('Impossible de synchroniser le catalogue des molecules:', message)
  }
}

function mapStatusToFlag(status: AnalysisStatus): AbnormalFlag | null {
  switch (status) {
    case 'critique':
      return 'HH'
    case 'anormal':
      return 'H'
    case 'limite':
      return 'H'
    default:
      return null
  }
}

function isStatusAbnormal(status: AnalysisStatus): boolean {
  return status === 'anormal' || status === 'critique' || status === 'limite'
}

function sortLabResults(results: LabResult[]): LabResult[] {
  return [...results].sort((left, right) => {
    const dateDiff = new Date(right.test_date).getTime() - new Date(left.test_date).getTime()
    if (dateDiff !== 0) {
      return dateDiff
    }

    return right.created_at.localeCompare(left.created_at)
  })
}

function mapAnalysisRowToLabResult(
  row: AnalysisResultRow,
  document: MedicalDocumentLookup | undefined
): LabResult {
  const metadata = getAnalysisRowMetadata(row)
  const normalizedTestName = normalizeLabTestName(
    pickBestString(metadata.raw_name, row.nom_analyse, metadata.canonical_name)
  )
  const testName = applyLabUnitQualifier(normalizedTestName, row.unite)
  const referenceRangeLabel = pickBestString(metadata.reference_range, row.valeur_reference)
  const referenceRange = parseReferenceRange(referenceRangeLabel)
  const numericValue = parseMedicalNumber(row.valeur)
  const normalizedMeasurement = normalizeLabMeasurement(
    testName,
    numericValue,
    row.unite,
    referenceRange.reference_min,
    referenceRange.reference_max
  )
  const abnormalFlag =
    calculateAbnormalFlagFromRange(numericValue, referenceRange.reference_min, referenceRange.reference_max) ||
    mapStatusToFlag(row.statut)

  return {
    id: row.id,
    document_id: row.document_id,
    patient_id: row.patient_id,
    test_name: testName,
    test_code: null,
    test_category: coerceLabTestCategory(metadata.test_category) || inferLabTestCategory(testName),
    result_value: numericValue as never,
    result_text: row.valeur,
    unit: row.unite,
    raw_unit: normalizedMeasurement.rawUnit,
    normalized_unit: normalizedMeasurement.normalizedUnit,
    normalized_value: normalizedMeasurement.normalizedValue,
    normalized_reference_min: normalizedMeasurement.normalizedReferenceMin,
    normalized_reference_max: normalizedMeasurement.normalizedReferenceMax,
    unit_group_key: normalizedMeasurement.unitGroupKey,
    unit_group_label: normalizedMeasurement.unitGroupLabel,
    unit_status: normalizedMeasurement.unitStatus,
    is_unit_converted: normalizedMeasurement.isUnitConverted,
    unit_warning: normalizedMeasurement.unitWarning,
    reference_range: referenceRangeLabel,
    raw_reference_range: referenceRangeLabel,
    reference_min: referenceRange.reference_min,
    reference_max: referenceRange.reference_max,
    is_abnormal: abnormalFlag !== null || isStatusAbnormal(row.statut),
    abnormal_flag: abnormalFlag,
    test_date: row.date_analyse,
    laboratory_name: pickBestString(metadata.laboratory_name, row.laboratoire, document?.medical_facility),
    doctor_name: pickBestString(metadata.doctor_name, document?.practitioner_name),
    specialty: pickBestString(metadata.specialty, document?.specialty),
    notes: row.commentaire,
    document_url: document?.file_url || null,
    source_document_title: document?.title || document?.file_name || 'Document médical',
    source_file_name: document?.file_name || null,
    created_at: row.created_at,
    updated_at: row.updated_at
  } as LabResult
}

async function getMappedLabResults(patientId: string): Promise<LabResult[]> {
  const pageSize = 1000
  const allRows: AnalysisResultRow[] = []
  let pageIndex = 0

  while (true) {
    const from = pageIndex * pageSize
    const to = from + pageSize - 1
    const { data: pageRows, error: pageError } = await supabase
      .from('analyses_resultats')
      .select('id, document_id, patient_id, nom_analyse, valeur, unite, valeur_reference, statut, date_analyse, laboratoire, commentaire, metadata, created_at, updated_at')
      .eq('patient_id', patientId)
      .order('date_analyse', { ascending: false })
      .order('created_at', { ascending: false })
      .range(from, to)

    if (pageError) {
      throw new Error(`Erreur lors de la rÃ©cupÃ©ration des analyses biologiques: ${pageError.message}`)
    }

    const currentRows = (pageRows || []) as AnalysisResultRow[]
    allRows.push(...currentRows)

    if (currentRows.length < pageSize) {
      break
    }

    pageIndex += 1
  }

  if (allRows.length === 0) {
    return []
  }

  const allDocumentIds = [...new Set(allRows.map((row) => row.document_id).filter(Boolean))]
  const resolvedDocumentMap = new Map<string, MedicalDocumentLookup>()

  if (allDocumentIds.length > 0) {
    const chunkSize = 200
    const collectedDocuments: MedicalDocumentLookup[] = []

    for (let index = 0; index < allDocumentIds.length; index += chunkSize) {
      const chunk = allDocumentIds.slice(index, index + chunkSize)
      const { data: documents, error: documentError } = await supabase
        .from('medical_documents')
        .select('id, title, file_name, file_url, practitioner_name, medical_facility, specialty')
        .in('id', chunk)

      if (documentError) {
        console.warn('Impossible de charger les mÃ©tadonnÃ©es des documents liÃ©s aux analyses:', documentError.message)
        continue
      }

      collectedDocuments.push(...((documents || []) as MedicalDocumentLookup[]))
    }

    const resolvedDocuments = await resolveMedicalDocumentUrls(collectedDocuments)
    resolvedDocuments.forEach((document) => {
      resolvedDocumentMap.set(document.id, document)
    })
  }

  return sortLabResults(
    allRows.map((row) => mapAnalysisRowToLabResult(row, resolvedDocumentMap.get(row.document_id)))
  )

  const { data: analysisRows, error } = await supabase
    .from('analyses_resultats')
    .select('id, document_id, patient_id, nom_analyse, valeur, unite, valeur_reference, statut, date_analyse, laboratoire, commentaire, metadata, created_at, updated_at')
    .eq('patient_id', patientId)
    .order('date_analyse', { ascending: false })
    .order('created_at', { ascending: false })

  if (error) {
    throw new Error(`Erreur lors de la récupération des analyses biologiques: ${error.message}`)
  }

  const rows = (analysisRows || []) as AnalysisResultRow[]
  if (rows.length === 0) {
    return []
  }

  const documentIds = [...new Set(rows.map((row) => row.document_id).filter(Boolean))]
  let documentMap = new Map<string, MedicalDocumentLookup>()

  if (documentIds.length > 0) {
    const { data: documents, error: documentError } = await supabase
      .from('medical_documents')
      .select('id, title, file_name, file_url, practitioner_name, medical_facility, specialty')
      .in('id', documentIds)

    if (documentError) {
      console.warn('Impossible de charger les métadonnées des documents liés aux analyses:', documentError.message)
    } else {
      const resolvedDocuments = await resolveMedicalDocumentUrls((documents || []) as MedicalDocumentLookup[])
      documentMap = new Map(resolvedDocuments.map((document) => [document.id, document]))
    }
  }

  return sortLabResults(rows.map((row) => mapAnalysisRowToLabResult(row, documentMap.get(row.document_id))))
}

export async function getLabResults(
  patientId: string,
  limit: number | null = 200
): Promise<LabResult[]> {
  const results = await getMappedLabResults(patientId)

  if (limit === null) {
    return results
  }

  return results.slice(0, limit)
}

interface NormalizedManualLabResultInput {
  patient_id: string
  test_date: string
  raw_name: string
  canonical_name: string
  result_value: string
  unit: string | null
  reference_range: string | null
  reference_min: number | null
  reference_max: number | null
  doctor_name: string | null
  laboratory_name: string | null
  specialty: string | null
  notes: string | null
  test_category: LabTestCategory
  abnormal_flag: AbnormalFlag | null
  status: AnalysisStatus
}

function normalizeManualLabResultInput(input: ManualLabResultInput): NormalizedManualLabResultInput {
  const canonicalName = normalizeLabTestName(input.test_name)
  const rawName = pickBestString(input.test_name, canonicalName)

  if (!rawName) {
    throw new Error('Le nom de la molecule est requis')
  }

  const normalizedResultValue = input.result_value.trim()

  if (!normalizedResultValue) {
    throw new Error('La valeur du resultat est requise')
  }

  const normalizedUnit = pickBestString(input.unit)
  const normalizedDoctorName = pickBestString(input.doctor_name)
  const normalizedLaboratoryName = pickBestString(input.laboratory_name)
  const normalizedSpecialty = pickBestString(input.specialty)
  const normalizedNotes = pickBestString(input.notes)
  const testCategory = inferLabTestCategory(canonicalName)
  const referenceMin = typeof input.reference_min === 'number' && Number.isFinite(input.reference_min)
    ? input.reference_min
    : null
  const referenceMax = typeof input.reference_max === 'number' && Number.isFinite(input.reference_max)
    ? input.reference_max
    : null
  const referenceRange = buildReferenceRangeLabel(referenceMin, referenceMax, input.reference_range)
  const numericValue = parseMedicalNumber(normalizedResultValue)
  const abnormalFlag = calculateAbnormalFlagFromRange(numericValue, referenceMin, referenceMax)
  const status = mapAbnormalFlagToStatus(abnormalFlag)

  return {
    patient_id: input.patient_id,
    test_date: input.test_date,
    raw_name: rawName,
    canonical_name: canonicalName,
    result_value: normalizedResultValue,
    unit: normalizedUnit,
    reference_range: referenceRange,
    reference_min: referenceMin,
    reference_max: referenceMax,
    doctor_name: normalizedDoctorName,
    laboratory_name: normalizedLaboratoryName,
    specialty: normalizedSpecialty,
    notes: normalizedNotes,
    test_category: testCategory,
    abnormal_flag: abnormalFlag,
    status
  }
}

function buildManualLabDocumentMetadata(entries: NormalizedManualLabResultInput[]) {
  const entryLabels = entries.map((entry) => entry.canonical_name)

  return {
    system_record_type: 'manual_lab_entry',
    entry_mode: 'manual',
    test_count: entries.length,
    test_names: entryLabels,
    doctor_name: entries[0]?.doctor_name || null,
    laboratory_name: entries[0]?.laboratory_name || null,
    specialty: entries[0]?.specialty || null,
    notes: entries.map((entry) => entry.notes).filter(Boolean),
    created_from: 'lab_results_manual_form_batch'
  }
}

export async function createManualLabResultBatch(inputs: ManualLabResultInput[]): Promise<LabResult[]> {
  if (inputs.length === 0) {
    return []
  }

  const normalizedEntries = inputs.map(normalizeManualLabResultInput)
  const firstEntry = normalizedEntries[0]

  if (!normalizedEntries.every((entry) => entry.patient_id === firstEntry.patient_id)) {
    throw new Error('Toutes les analyses du lot doivent appartenir au meme patient')
  }

  const timestampSuffix =
    typeof globalThis.crypto?.randomUUID === 'function'
      ? globalThis.crypto.randomUUID().slice(0, 8)
      : Math.random().toString(36).slice(2, 10)
  const baseFileName = `saisie-bio-${firstEntry.test_date}-${slugifyManualLabTestName(firstEntry.canonical_name)}-${timestampSuffix}.txt`
  const documentMetadata = buildManualLabDocumentMetadata(normalizedEntries)

  const { data: documentRow, error: documentError } = await supabase
    .from('medical_documents')
    .insert({
      patient_id: firstEntry.patient_id,
      document_type: 'blood_test',
      title:
        normalizedEntries.length === 1
          ? `Saisie manuelle - ${firstEntry.canonical_name}`
          : `Saisie manuelle - bilan du ${firstEntry.test_date}`,
      description:
        normalizedEntries.length === 1
          ? firstEntry.notes || 'Resultat biologique saisi manuellement'
          : `${normalizedEntries.length} resultats biologiques saisis manuellement`,
      file_name: baseFileName,
      file_url: null,
      file_size_bytes: 0,
      mime_type: 'text/plain',
      document_date: firstEntry.test_date,
      practitioner_name: firstEntry.doctor_name,
      medical_facility: firstEntry.laboratory_name,
      specialty: firstEntry.specialty || 'Biologie',
      extracted_data: documentMetadata,
      tags: ['manual-entry', 'biologie'],
      is_urgent: false,
      is_confidential: true,
      shared_with_doctors: []
    })
    .select('id, title, file_name, file_url, practitioner_name, medical_facility, specialty')
    .single()

  if (documentError) {
    throw new Error(`Erreur lors de la creation du support biologique: ${documentError.message}`)
  }

  const analysisRowsPayload = normalizedEntries.map((entry) => ({
    document_id: documentRow.id,
    patient_id: entry.patient_id,
    nom_analyse: entry.raw_name,
    valeur: entry.result_value,
    unite: entry.unit,
    valeur_reference: entry.reference_range,
    statut: entry.status,
    date_analyse: entry.test_date,
    laboratoire: entry.laboratory_name,
    commentaire: entry.notes,
    metadata: {
      ...documentMetadata,
      canonical_name: entry.canonical_name,
      raw_name: entry.raw_name,
      reference_range: entry.reference_range,
      test_category: entry.test_category,
      unit: entry.unit,
      raw_value: entry.result_value,
      status: entry.status,
      specialty: entry.specialty
    }
  }))

  const { data: analysisRows, error: analysisError } = await supabase
    .from('analyses_resultats')
    .insert(analysisRowsPayload)
    .select('id, document_id, patient_id, nom_analyse, valeur, unite, valeur_reference, statut, date_analyse, laboratoire, commentaire, metadata, created_at, updated_at')

  if (analysisError) {
    await supabase.from('medical_documents').delete().eq('id', documentRow.id)
    throw new Error(`Erreur lors de la creation du resultat biologique: ${analysisError.message}`)
  }

  await Promise.all(
    normalizedEntries.map((entry) =>
      syncManualLabCatalogRow({
        documentId: documentRow.id,
        patientId: entry.patient_id,
        testDate: entry.test_date,
        rawName: entry.raw_name,
        canonicalName: entry.canonical_name,
        testCategory: entry.test_category,
        resultValue: entry.result_value,
        unit: entry.unit,
        referenceRange: entry.reference_range,
        status: entry.status,
        laboratoryName: entry.laboratory_name,
        doctorName: entry.doctor_name,
        specialty: entry.specialty
      })
    )
  )

  return ((analysisRows || []) as AnalysisResultRow[]).map((analysisRow) =>
    mapAnalysisRowToLabResult(analysisRow, documentRow as MedicalDocumentLookup)
  )
}

export async function createManualLabResult(input: ManualLabResultInput): Promise<LabResult> {
  const results = await createManualLabResultBatch([input])
  const [result] = results

  if (!result) {
    throw new Error('Impossible de creer le resultat biologique')
  }

  return result
}

export async function getLabResultsByTest(
  patientId: string,
  testName: string,
  limit = 50
): Promise<LabResult[]> {
  const canonicalName = normalizeLabTestName(testName)
  const results = await getMappedLabResults(patientId)

  return results
    .filter((result) => result.test_name === canonicalName)
    .slice(0, limit)
}

export function buildLabTrendsDataFromResults(
  results: LabResult[],
  testNames?: string[]
): LabTrendData[] {
  const normalizedFilters = (testNames || []).map((testName) => normalizeLabTestName(testName))
  const filteredResults = results.filter((result) => {
    if (normalizedFilters.length === 0) {
      return true
    }

    return normalizedFilters.includes(result.test_name)
  })

  const groupedResults = new Map<string, LabResult[]>()
  const groupsByTestName = new Map<string, Set<string>>()

  for (const result of filteredResults) {
    const comparableValue = result.normalized_value ?? result.result_value

    if (comparableValue === null || Number.isNaN(comparableValue)) {
      continue
    }

    const unitGroupKey = result.unit_group_key || `${result.test_name}::${result.unit || 'no-unit'}`

    if (!groupedResults.has(unitGroupKey)) {
      groupedResults.set(unitGroupKey, [])
    }

    if (!groupsByTestName.has(result.test_name)) {
      groupsByTestName.set(result.test_name, new Set())
    }

    groupsByTestName.get(result.test_name)!.add(unitGroupKey)
    groupedResults.get(unitGroupKey)!.push(result)
  }

  const trends: LabTrendData[] = []

  for (const testResults of groupedResults.values()) {
    const orderedResults = [...testResults].sort(
      (left, right) => new Date(left.test_date).getTime() - new Date(right.test_date).getTime()
    )
    const latestResult = orderedResults[orderedResults.length - 1]

    if (!latestResult) {
      continue
    }

    const testName = latestResult.test_name
    const unitLabel = latestResult.normalized_unit || latestResult.unit_group_label || latestResult.unit || ''
    const groupCount = groupsByTestName.get(testName)?.size || 1
    const seriesLabel = groupCount > 1 ? `${testName} (${unitLabel || 'sans unite'})` : testName
    const rawUnits = [...new Set(orderedResults.map((result) => result.raw_unit || result.unit).filter(Boolean))]
    const hasUnitConversion = orderedResults.some((result) => Boolean(result.is_unit_converted))
    const hasUnitMismatch =
      groupCount > 1 ||
      orderedResults.some((result) => result.unit_status === 'unsupported' || result.unit_status === 'missing')

    trends.push({
      test_name: testName,
      series_key: latestResult.unit_group_key || `${testName}::${unitLabel}`,
      series_label: seriesLabel,
      raw_units: rawUnits,
      has_unit_conversion: hasUnitConversion,
      has_unit_mismatch: hasUnitMismatch,
      test_category: latestResult.test_category || inferLabTestCategory(testName),
      unit: unitLabel,
      reference_min: latestResult.normalized_reference_min ?? latestResult.reference_min,
      reference_max: latestResult.normalized_reference_max ?? latestResult.reference_max,
      latest_result: latestResult,
      results: orderedResults.map((result) => ({
        date: result.test_date,
        value: (result.normalized_value ?? result.result_value) as number,
        is_abnormal: result.is_abnormal,
        abnormal_flag: result.abnormal_flag
      }))
    })
  }

  return trends.sort((left, right) => left.test_name.localeCompare(right.test_name, 'fr'))
}

export async function getLabTrendsData(
  patientId: string,
  testNames?: string[]
): Promise<LabTrendData[]> {
  const results = await getMappedLabResults(patientId)
  return buildLabTrendsDataFromResults(results, testNames)
}

export async function getAvailableLabTests(patientId: string): Promise<string[]> {
  const results = await getMappedLabResults(patientId)
  return [...new Set(results.map((result) => result.test_name))].sort((left, right) =>
    left.localeCompare(right, 'fr')
  )
}

export function getAvailableLabCategories(results: LabResult[]): LabTestCategory[] {
  return [...new Set(results.map((result) => result.test_category).filter(Boolean))]
    .sort((left, right) => (left || '').localeCompare(right || '', 'fr')) as LabTestCategory[]
}

export function summarizeLabUnitInsights(results: LabResult[]): LabUnitInsightSummary {
  const testsByUnitGroup = new Map<string, Map<string, LabResult[]>>()
  let convertedResultsCount = 0
  let missingUnitResultsCount = 0
  let unsupportedUnitResultsCount = 0

  results.forEach((result) => {
    if (result.is_unit_converted) {
      convertedResultsCount += 1
    }

    if (result.unit_status === 'missing') {
      missingUnitResultsCount += 1
    }

    if (result.unit_status === 'unsupported') {
      unsupportedUnitResultsCount += 1
    }

    if (!testsByUnitGroup.has(result.test_name)) {
      testsByUnitGroup.set(result.test_name, new Map())
    }

    const unitGroupKey = result.unit_group_key || `${result.test_name}::${result.unit || 'no-unit'}`
    const unitGroupLabel = result.unit_group_label || result.normalized_unit || result.unit || 'Sans unite'
    const groups = testsByUnitGroup.get(result.test_name)!

    if (!groups.has(`${unitGroupKey}::${unitGroupLabel}`)) {
      groups.set(`${unitGroupKey}::${unitGroupLabel}`, [])
    }

    groups.get(`${unitGroupKey}::${unitGroupLabel}`)!.push(result)
  })

  const mixedUnitTests = [...testsByUnitGroup.entries()]
    .map(([testName, groups]) => {
      if (groups.size <= 1) {
        return null
      }

      const entries = [...groups.entries()]
      const unitLabels = entries.map(([groupKey]) => groupKey.split('::').at(-1) || 'Sans unite')
      const groupResults = entries.flatMap(([, groupResults]) => groupResults)

      return {
        test_name: testName,
        unit_labels: unitLabels,
        converted_results: groupResults.filter((result) => Boolean(result.is_unit_converted)).length,
        missing_unit_results: groupResults.filter((result) => result.unit_status === 'missing').length,
        unsupported_unit_results: groupResults.filter((result) => result.unit_status === 'unsupported').length
      }
    })
    .filter(Boolean) as LabUnitMixWarning[]

  return {
    converted_results_count: convertedResultsCount,
    missing_unit_results_count: missingUnitResultsCount,
    unsupported_unit_results_count: unsupportedUnitResultsCount,
    mixed_unit_tests: mixedUnitTests.sort((left, right) => left.test_name.localeCompare(right.test_name, 'fr'))
  }
}

export function matchesLabDiseaseProfile(result: LabResult, profile: LabDiseaseProfile): boolean {
  if (profile.tests.includes(result.test_name)) {
    return true
  }

  return Boolean(result.test_category && profile.categories.includes(result.test_category))
}

export async function getRecentAbnormalResults(
  patientId: string,
  daysBack = 30
): Promise<LabResult[]> {
  const cutoffDate = new Date()
  cutoffDate.setDate(cutoffDate.getDate() - daysBack)

  const results = await getMappedLabResults(patientId)

  return results.filter((result) => {
    if (!result.is_abnormal) {
      return false
    }

    return new Date(result.test_date).getTime() >= cutoffDate.getTime()
  })
}

export function buildLabResultsMatrix(
  results: LabResult[],
  options?: {
    searchTerm?: string
    selectedTests?: string[]
  }
): LabMatrixData {
  const searchTerm = normalizeSearchText(options?.searchTerm)
  const selectedTests = (options?.selectedTests || []).map((testName) => normalizeLabTestName(testName))

  const filteredResults = results.filter((result) => {
    const matchesSelectedTests =
      selectedTests.length === 0 || selectedTests.includes(result.test_name)

    if (!matchesSelectedTests) {
      return false
    }

    if (!searchTerm) {
      return true
    }

    const searchHaystack = normalizeSearchText([
      result.test_name,
      result.result_text,
      result.source_document_title,
      result.doctor_name,
      result.laboratory_name
    ].filter(Boolean).join(' '))

    return searchHaystack.includes(searchTerm)
  })

  const columnUsage = new Map<string, number>()
  const rowsById = new Map<string, LabMatrixRow>()

  for (const result of filteredResults) {
    columnUsage.set(result.test_name, (columnUsage.get(result.test_name) || 0) + 1)

    const rowKey = `${result.test_date}::${result.document_id || result.id}`
    const existingRow = rowsById.get(rowKey)

    if (!existingRow) {
      rowsById.set(rowKey, {
        row_id: rowKey,
        test_date: result.test_date,
        document_id: result.document_id || null,
        document_title: result.source_document_title || 'Document médical',
        laboratory_name: result.laboratory_name || null,
        doctor_name: result.doctor_name || null,
        specialty: result.specialty || null,
        document_url: result.document_url || null,
        cells: {
          [result.test_name]: {
            test_name: result.test_name,
            result
          }
        }
      })
      continue
    }

    const existingCell = existingRow.cells[result.test_name]

    if (!existingRow.laboratory_name && result.laboratory_name) {
      existingRow.laboratory_name = result.laboratory_name
    }

    if (!existingRow.doctor_name && result.doctor_name) {
      existingRow.doctor_name = result.doctor_name
    }

    if (!existingRow.specialty && result.specialty) {
      existingRow.specialty = result.specialty
    }

    if (!existingRow.document_url && result.document_url) {
      existingRow.document_url = result.document_url
    }

    if (!existingCell || existingCell.result.created_at < result.created_at) {
      existingRow.cells[result.test_name] = {
        test_name: result.test_name,
        result
      }
    }
  }

  const columns = [...columnUsage.entries()]
    .sort((left, right) => {
      if (right[1] !== left[1]) {
        return right[1] - left[1]
      }

      return left[0].localeCompare(right[0], 'fr')
    })
    .map(([testName]) => testName)

  const rows = [...rowsById.values()].sort((left, right) => {
    const dateDiff = new Date(right.test_date).getTime() - new Date(left.test_date).getTime()
    if (dateDiff !== 0) {
      return dateDiff
    }

    return left.document_title.localeCompare(right.document_title, 'fr')
  })

  return {
    columns,
    rows,
    filtered_results: sortLabResults(filteredResults)
  }
}

export function calculateTestTrend(
  results: LabResult[]
): 'improving' | 'worsening' | 'stable' | 'unknown' {
  const numericResults = results
    .filter((result) => {
      const comparableValue = result.normalized_value ?? result.result_value
      return comparableValue !== null && !Number.isNaN(comparableValue)
    })
    .sort((left, right) => new Date(left.test_date).getTime() - new Date(right.test_date).getTime())

  if (numericResults.length < 2) {
    return 'unknown'
  }

  const latest = numericResults[numericResults.length - 1]
  const previous = numericResults[numericResults.length - 2]
  const latestValue = latest.normalized_value ?? latest.result_value
  const previousValue = previous.normalized_value ?? previous.result_value
  const latestReferenceMin = latest.normalized_reference_min ?? latest.reference_min
  const latestReferenceMax = latest.normalized_reference_max ?? latest.reference_max
  const previousReferenceMin = previous.normalized_reference_min ?? previous.reference_min
  const previousReferenceMax = previous.normalized_reference_max ?? previous.reference_max

  if (latestValue === null || previousValue === null || previousValue === 0) {
    return 'unknown'
  }

  if (latestReferenceMin !== null && latestReferenceMax !== null) {
    const latestDistance = latestValue < latestReferenceMin
      ? latestReferenceMin - latestValue
      : latestValue > latestReferenceMax
        ? latestValue - latestReferenceMax
        : 0

    const previousDistance = previousReferenceMin !== null && previousReferenceMax !== null
      ? previousValue < previousReferenceMin
        ? previousReferenceMin - previousValue
        : previousValue > previousReferenceMax
          ? previousValue - previousReferenceMax
          : 0
      : 0

    if (latestDistance === 0 && previousDistance > 0) return 'improving'
    if (latestDistance > 0 && previousDistance === 0) return 'worsening'
    if (latestDistance < previousDistance * 0.9) return 'improving'
    if (latestDistance > previousDistance * 1.1) return 'worsening'
    return 'stable'
  }

  const percentChange = Math.abs((latestValue - previousValue) / previousValue)
  if (percentChange < 0.05) {
    return 'stable'
  }

  return 'unknown'
}

export function formatLabValue(
  value: number | null,
  unit: string | null,
  fallbackText?: string | null
): string {
  if (value === null || Number.isNaN(value)) {
    return fallbackText || '--'
  }

  let formattedValue = ''

  if (Math.abs(value) < 1 && value !== 0) {
    formattedValue = value.toFixed(2)
  } else if (Math.abs(value) < 10) {
    formattedValue = value.toFixed(1)
  } else {
    formattedValue = Number.isInteger(value) ? value.toString() : value.toFixed(2).replace(/\.?0+$/, '')
  }

  return unit ? `${formattedValue} ${unit}` : formattedValue
}

export function getLabValueColor(
  value: number | null,
  refMin: number | null,
  refMax: number | null,
  abnormalFlag?: AbnormalFlag | null
): string {
  if (abnormalFlag) {
    return abnormalFlag === 'HH' || abnormalFlag === 'LL' ? 'text-red-400' : 'text-orange-400'
  }

  if (value === null || Number.isNaN(value)) {
    return 'text-slate-300'
  }

  if (refMin !== null && value < refMin) {
    return value < refMin * 0.5 ? 'text-red-400' : 'text-orange-400'
  }

  if (refMax !== null && value > refMax) {
    return value > refMax * 2 ? 'text-red-400' : 'text-orange-400'
  }

  if (refMin !== null || refMax !== null) {
    return 'text-green-400'
  }

  return 'text-slate-300'
}

export const COMMON_LAB_TESTS: Array<{
  name: string
  category: LabTestCategory
  unit: string
  description: string
}> = [
  { name: 'CRP', category: 'Inflammation', unit: 'mg/L', description: 'Protéine C-réactive' },
  { name: 'VS', category: 'Inflammation', unit: 'mm/h', description: 'Vitesse de sédimentation' },
  { name: 'Ferritine', category: 'Fer', unit: 'µg/L', description: 'Réserves de fer' },
  { name: 'Hémoglobine', category: 'Hématologie', unit: 'g/dL', description: 'Transport de l’oxygène' },
  { name: 'Leucocytes', category: 'Hématologie', unit: 'G/L', description: 'Globules blancs' },
  { name: 'Plaquettes', category: 'Hématologie', unit: 'G/L', description: 'Plaquettes sanguines' },
  { name: 'Glycémie', category: 'Métabolisme', unit: 'g/L', description: 'Taux de sucre sanguin' },
  { name: 'HbA1c', category: 'Métabolisme', unit: '%', description: 'Moyenne glycémique des 3 derniers mois' },
  { name: 'Cholestérol total', category: 'Lipides', unit: 'g/L', description: 'Bilan lipidique' },
  { name: 'Créatinine', category: 'Rein', unit: 'mg/L', description: 'Fonction rénale' },
  { name: 'ALAT', category: 'Foie', unit: 'UI/L', description: 'Cytolyse hépatique' },
  { name: 'TSH', category: 'Thyroïde', unit: 'mUI/L', description: 'Fonction thyroïdienne' }
]
